import './bootstrap';
import '../css/app.css';

// Vue 3 imports
import { createApp, h, nextTick } from 'vue';
import { pinia } from './pinia';
import { createInertiaApp, Link, Head, router } from '@inertiajs/vue3';

// Patch Inertia's Head component to directly set document.title (fixes Vue 3 migration)
Head.data = function () { return {}; };
Head.beforeUnmount = function () {};
Head.methods = {};
Head.render = function () {
    try {
        const nodes = this.$slots.default ? this.$slots.default() : [];
        let titleText = this.title || '';
        const extractText = (children) => {
            if (!children) return '';
            if (typeof children === 'string') return children;
            if (typeof children === 'number') return String(children);
            if (Array.isArray(children)) {
                return children.map(c => {
                    if (typeof c === 'string') return c;
                    if (typeof c === 'number') return String(c);
                    if (c && c.children != null) return extractText(c.children);
                    return '';
                }).join('');
            }
            return '';
        };
        const processNodes = (nodeList) => {
            if (!Array.isArray(nodeList)) return;
            for (const node of nodeList) {
                if (!node) continue;
                if (typeof node.type === 'symbol') {
                    if (node.children && Array.isArray(node.children)) processNodes(node.children);
                    continue;
                }
                if (node.type === 'title') {
                    titleText = extractText(node.children);
                } else if (node.type === 'meta' && node.props) {
                    const sel = node.props.name
                        ? `meta[name="${node.props.name}"]`
                        : node.props.property
                            ? `meta[property="${node.props.property}"]`
                            : null;
                    if (sel) {
                        const el = document.head.querySelector(sel);
                        if (el && node.props.content !== undefined) {
                            el.setAttribute('content', String(node.props.content));
                        }
                    }
                }
            }
        };
        processNodes(nodes);
        if (titleText) document.title = titleText;
    } catch (_) {}
    return null;
};

// Plugins
import ui from '@nuxt/ui/vue-plugin';
import { createHead } from '@unhead/vue/client';
import FloatingVue from 'floating-vue';
import 'floating-vue/dist/style.css';
import ToastPlugin from 'vue-toast-notification';
// Toast styles are now in app.css using Tailwind

// Composables & Utilities
import dayjs from 'dayjs';
import moment from 'moment-mini-ts';
import numeral from 'numeral';

// Icons
import { FontAwesomeIcon } from './icons';

// Ziggy
import { ZiggyVue } from 'ziggy-js';
import { Ziggy } from './ziggy';
import { withRuntimeOrigin } from './utils/runtimeUrls';

// Stores
import { 
    useMarketsStore, 
    useOrdersStore, 
    useWalletsStore, 
    useUserStore, 
    useSocketStore, 
    useSettingsStore,
    useThemeStore 
} from './stores';

// Components
import HeaderCaption from '@/Components/HeaderCaption.vue';
import ErrorPage from './Pages/Error/ErrorPage.vue';
import TRichSelect from '@/Components/TRichSelect.vue';
import TRichUserSelect from '@/Components/TRichUserSelect.vue';
import TDropdown from '@/Components/TDropdown.vue';
import TToggle from '@/Components/TToggle.vue';
import TRadio from '@/Components/TRadio.vue';
import TCheckbox from '@/Components/TCheckbox.vue';
import UDatePicker from '@/Components/UDatePicker.vue';
import UTimePicker from '@/Components/UTimePicker.vue';
// Persistent WebSocket: mounted once at the app root (sibling of the Inertia page)
// so the Echo connection + private channel survive page navigation instead of
// being torn down and re-established on every visit.
import Socket from '@/Components/Jetstream/Socket.vue';

// Global utilities
window.dayjs = dayjs;
window.moment = moment;
window.numeral = numeral;

// I18n with lazy loading from API v2
import i18n, { loadBundle, loadFromCacheSync, getLocale } from './plugins/i18n';
import { getNamespacesForPath } from './plugins/translationNamespaces';

const head = createHead();

// Event bus for Vue 3 (replacement for Vue 2 event bus)
import mitt from 'mitt';
const emitter = mitt();
window.VueWorker = emitter;

function setupEchoConnectionMonitor() {
    if (typeof window === 'undefined' || window.__propEchoMonitorInitialized) return;
    window.__propEchoMonitorInitialized = true;

    const emitState = (state, previous = null) => {
        window.dispatchEvent(new CustomEvent('prop-echo-connection', {
            detail: { state, previous, at: Date.now() },
        }));
    };

    const bindConnection = (connection) => {
        if (!connection || connection.__propBound) return true;
        connection.__propBound = true;

        if (typeof connection.bind === 'function') {
            connection.bind('state_change', ({ previous, current }) => {
                emitState(current, previous);
            });
            connection.bind('connected', () => emitState('connected'));
            connection.bind('disconnected', () => emitState('disconnected'));
            connection.bind('unavailable', () => emitState('unavailable'));
            return true;
        }

        return false;
    };

    const tryAttach = () => {
        const echo = window.Echo;
        const connection = echo?.connector?.pusher?.connection || echo?.connector?.socket;
        return bindConnection(connection);
    };

    if (tryAttach()) return;

    let attempts = 0;
    const timer = setInterval(() => {
        attempts += 1;
        if (tryAttach() || attempts > 20) {
            clearInterval(timer);
        }
    }, 1000);
}

// Create Inertia App
createInertiaApp({
    title: title => {
        if (title && typeof document !== 'undefined') document.title = title;
        return title;
    },
    resolve: name => {
        window.__inertiaCurrentComponent = name;
        if (name === 'Error/ErrorPage') return { default: ErrorPage };
        // Lazy-load pages to reduce initial bundle and TBT (only current page loads on first paint)
        const pages = import.meta.glob('./Pages/**/*.vue');
        const normalizedName = String(name)
            .replace(/^\/+/, '')
            .replace(/^Pages\//, '')
            .replace(/\.vue$/, '');
        const path = `./Pages/${normalizedName}.vue`;
        const loader = pages[path];
        if (loader) return loader();
        throw new Error(`Inertia page not found: ${path}`);
    },
    setup({ el, App, props, plugin }) {
        const app = createApp({ render: () => [h(App, props), h(Socket)] });
        // Prefer locale from server (Inertia shared props) so all pages use the same language
        const pageProps = props.initialPage?.props ?? {};
        const locale = pageProps.locale ?? getLocale();
        if (i18n.global.locale.value !== locale) {
            i18n.global.locale.value = locale;
        }
        window.LOCALE = locale;
        window.LANGUAGE = locale;
        const metaEl = document.querySelector('meta[name="site-language"]');
        if (metaEl && metaEl.getAttribute('content') !== locale) {
            metaEl.setAttribute('content', locale);
        }
        // Use plugins
        app.use(plugin);
        app.use(pinia);
        app.use(i18n);
        app.use(head);
        try {
            app.use(ui);
        } catch (err) {
            console.warn('[app] Nuxt UI plugin install failed, continuing without plugin context.', err);
        }
        app.use(ZiggyVue, withRuntimeOrigin(Ziggy));
        app.use(FloatingVue);
        app.use(ToastPlugin, {
            position: 'bottom-right',
            duration: 5000,
            dismissible: true
        });

        // Register global components
        app.component('font-awesome-icon', FontAwesomeIcon);
        app.component('HeaderCaption', HeaderCaption);
        app.component('Link', Link);
        app.component('Head', Head);
        app.component('t-rich-select', TRichSelect);
        app.component('TRichSelect', TRichSelect);
        app.component('t-rich-user-select', TRichUserSelect);
        app.component('TRichUserSelect', TRichUserSelect);
        app.component('t-dropdown', TDropdown);
        app.component('TDropdown', TDropdown);
        app.component('t-toggle', TToggle);
        app.component('TToggle', TToggle);
        app.component('t-radio', TRadio);
        app.component('TRadio', TRadio);
        app.component('t-checkbox', TCheckbox);
        app.component('TCheckbox', TCheckbox);
        app.component('UDatePicker', UDatePicker);
        app.component('UTimePicker', UTimePicker);

        // Global date formatters (avoid raw ISO like 2026-03-02T10:50:20.000000Z in admin)
        app.config.globalProperties.$formatDate = (date, options = {}) => {
            if (date == null || date === '') return options.fallback ?? '—';
            const d = dayjs(date);
            if (!d.isValid()) return options.fallback ?? '—';
            if (options.dateOnly) return d.format('YYYY-MM-DD');
            if (options.timeOnly) return d.format('HH:mm:ss');
            return d.format('D MMM YYYY, HH:mm');
        };
        app.config.globalProperties.$formatDateTime = (date, fallback = '—') => {
            if (date == null || date === '') return fallback;
            const d = dayjs(date);
            return d.isValid() ? d.format('D MMM YYYY, HH:mm') : fallback;
        };

        // Global properties
        app.config.globalProperties.$worker = emitter;
        app.config.globalProperties.$dayjs = dayjs;
        app.config.globalProperties.$moment = moment;
        app.config.globalProperties.$numeral = numeral;
        app.config.globalProperties.$cookies = window.$cookies;

        // Clipboard function (replacement for vue-clipboard2)
        app.config.globalProperties.$copyText = (text) => {
            return navigator.clipboard.writeText(text);
        };

        // Load initial namespaces: common + namespaces for current path (always include 'error' for error pages so locale is correct on first paint)
        const initialPath = typeof window !== 'undefined' ? window.location.pathname : '';
        let initialNamespaces = getNamespacesForPath(initialPath);
        if (props.initialPage?.component === 'Error/ErrorPage' && !initialNamespaces.includes('error')) {
            initialNamespaces = [...initialNamespaces, 'error'];
        }
        // Translation fetch must not reject the boot chain — a rejection here used to trigger
        // .catch(() => mount) while mount already ran / failed, causing a second mount and
        // Vue internal errors ("Cannot read properties of null (reading 'ce')").
        loadBundle(i18n, locale, initialNamespaces)
            .catch((err) => {
                console.warn('[i18n] Initial translation bundles failed, mounting anyway.', err);
            })
            .then(() => {
                if (!el) {
                    console.error('[app] Missing Inertia mount element.');
                    return;
                }
                app.mount(el);
                setupEchoConnectionMonitor();

                nextTick(() => {
                    try {
                        const themeStore = useThemeStore(pinia);
                        const initialMode = props.initialPage?.props?.theme || null;
                        const themeColors = props.initialPage?.props?.theme_colors || null;
                        themeStore.initTheme(initialMode, themeColors);
                    } catch (e) {
                        console.debug('Theme init skipped:', e);
                    }
                });

                // Helper: load translation namespaces for a URL and return a promise. Used so we can await before rendering.
                function loadNamespacesForUrl(url, locale) {
                    const path = typeof url === 'string' ? new URL(url, window.location.origin).pathname : '';
                    const namespaces = getNamespacesForPath(path);
                    if (namespaces.length) {
                        return loadBundle(i18n, locale || getLocale(), namespaces);
                    }
                    return Promise.resolve();
                }

                // Block navigation until translations for the target page are loaded (no flash of keys)
                const originalVisit = router.visit.bind(router);
                router.visit = function (url, options = {}) {
                    const path = typeof url === 'string' ? new URL(url, window.location.origin).pathname : '';
                    // Use current page locale so we load the same language the server will send (persists across pages)
                    const loc = router.page?.props?.locale ?? i18n.global.locale.value ?? getLocale();
                    const namespaces = getNamespacesForPath(path);
                    const doVisit = () => {
                        try {
                            const component = window.__inertiaCurrentComponent;
                            if (component) {
                                options = { ...options, headers: { ...(options.headers || {}), 'X-Inertia-Current-Component': component } };
                            }
                        } catch (_) {}
                        return originalVisit(url, options);
                    };
                    if (namespaces.length) {
                        return loadBundle(i18n, loc, namespaces).then(doVisit).catch(() => doVisit());
                    }
                    return doVisit();
                };

                // After navigate: sync locale/dir and load namespaces (covers back/forward and server redirects).
                // Translations are loaded from localStorage synchronously first to prevent key flash,
                // then verified from API asynchronously in the background.
                router.on('navigate', async (event) => {
                    const page = event?.detail?.page;
                    const pageProps = page?.props ?? {};
                    const pageLocale = pageProps.locale ?? null;
                    if (pageLocale) {
                        if (i18n.global.locale.value !== pageLocale) {
                            i18n.global.locale.value = pageLocale;
                        }
                        window.LOCALE = pageLocale;
                        window.LANGUAGE = pageLocale;
                        const meta = document.querySelector('meta[name="site-language"]');
                        if (meta && meta.getAttribute('content') !== pageLocale) meta.setAttribute('content', pageLocale);
                    }
                    const dir = pageProps.locale_direction || 'ltr';
                    document.documentElement.setAttribute('dir', dir);

                    const url = page?.url ?? window.location.href;
                    const path = typeof url === 'string' ? new URL(url, window.location.origin).pathname : window.location.pathname;
                    let namespaces = getNamespacesForPath(path);
                    if (page?.component === 'Error/ErrorPage' && !namespaces.includes('error')) {
                        namespaces = [...namespaces, 'error'];
                    }
                    const loc = pageLocale || i18n.global.locale.value || getLocale();
                    if (namespaces.length) {
                        loadFromCacheSync(i18n, loc, namespaces);
                        await loadBundle(i18n, loc, namespaces).catch(() => {});
                    }
                });

                window.Vue = app;
            });
    },
});
